import React, { useMemo } from 'react';
import EChartsReact from "@/components/EChartsReact";
import moment from 'moment';


const FacilityStackedChart = ({ data, type, style, title, facilities = [] }) => {
    // 使用useMemo来生成图表配置
    const option = useMemo(() => {
        if (!data || data.length === 0) {
            return {};
        }
        
        // 获取所有月份
        const months = [...new Set(data.map(item => item.month))].sort((a, b) => {
            // 将 YYYY-M 格式转换为 Date 对象进行比较
            const dateA = new Date(a + '-01'); // 添加日期部分
            const dateB = new Date(b + '-01');
            return dateA - dateB;
        });
        
        // 获取所有设施（去重）
        const facilitiesMap = new Map();
        data.forEach(item => {
            if (!facilitiesMap.has(item.facility_id)) {
                facilitiesMap.set(item.facility_id, {
                    id: item.facility_id,
                    name: item.facility_name
                });
            }
        });
        const facilitiesFromData = Array.from(facilitiesMap.values());
        
        // 创建 facility 颜色映射
        const facilityColorMap = {};
        facilities.forEach(facility => {
            facilityColorMap[facility.id] = facility.color;
        });
        
        // 准备堆叠图数据
        const series = facilitiesFromData.map((facility, index) => {
            return {
                name: facility.name,
                type: 'bar',
                stack: 'total',
                data: months.map(month => {
                    // 找到该设施在该月份的所有数据并求和
                    const monthDataItems = data.filter(item => 
                        item.facility_id === facility.id && item.month === month
                    );
                    return monthDataItems.reduce((sum, item) => sum + (item.usage || 0), 0);
                }),
                itemStyle: {
                    // 使用 facility 的颜色，如果没有则使用默认颜色
                    color: facilityColorMap[facility.id] || `hsl(${index * 360 / facilitiesFromData.length}, 70%, 50%)`
                }
            };
        });
        
        return {
            backgroundColor: '#fff',
            title: {
                text: title,
                left: 'center',
                top: 10,
                textStyle: {
                    fontSize: 12,
                    fontWeight: 'bold'
                }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                confine: false, // 允许tooltip超出canvas边界
                enterable: true, // 允许鼠标进入tooltip
                appendToBody: true, // 将tooltip添加到body，避免被容器限制
                extraCssText: 'z-index: 9999;', // 设置更高的z-index
                className: 'custom-tooltip',
                formatter: function(params) {
                    if (!params || params.length === 0) return '';
                    
                    const month = params[0].axisValue;
                    const unit = type === 'electricity' ? 'kWh' : 'MMBtu';
                    let result = `<strong>${moment(month).format('MMMM YYYY')}</strong><br/>`;
                    
                    // 按数值排序（降序）
                    const sortedParams = params
                        .filter(param => param.value > 0)
                        .sort((a, b) => b.value - a.value);
                    
                    let total = 0;
                    
                    if (sortedParams.length > 10) {
                        // 显示前10个，其余合并
                        const top10 = sortedParams.slice(0, 10);
                        const others = sortedParams.slice(10);
                        
                        top10.forEach(param => {
                            result += `${param.marker}${param.seriesName}: ${param.value.toLocaleString()} ${unit}<br/>`;
                            total += param.value;
                        });
                        
                        const othersTotal = others.reduce((sum, param) => sum + param.value, 0);
                        if (othersTotal > 0) {
                            result += `<span style="color: #999;">... and ${others.length} more facilities: ${othersTotal.toLocaleString()} ${unit}</span><br/>`;
                            total += othersTotal;
                        }
                        
                        result += `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #eee; color: #999; font-size: 11px;">Showing top 10 of ${sortedParams.length} facilities</div>`;
                    } else {
                        // 设施数量较少时，显示所有设施
                        sortedParams.forEach(param => {
                            result += `${param.marker}${param.seriesName}: ${param.value.toLocaleString()} ${unit}<br/>`;
                            total += param.value;
                        });
                    }
                    
                    result += `<br/><strong>Total: ${total.toLocaleString()} ${unit}</strong>`;
                    return result;
                }
            },
            legend: {
                show: false
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                top: '15%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: months,
                axisLabel: {
                    formatter: function(value) {
                        return moment(value).format('MMM YYYY');
                    }
                }
            },
            yAxis: {
                type: 'value',
                name: `Usage (${type === 'electricity' ? 'kWh' : 'MMBtu'})`,
                nameLocation: 'end',
                nameTextStyle: {
                    fontSize: 12,
                    padding: [10, 0, 0, 30]
                },
                axisLabel: {
                    formatter: function(value) {
                        return value.toLocaleString();
                    }
                }
            },
            series: series
        };
    }, [data, type, title, facilities]);

    return (
        <EChartsReact
            option={option}
            style={style || { width: '100%', height: '100%' }}
            notMerge={true} // 强制不合并，完全重新渲染
        />
    );
};

export default FacilityStackedChart; 